import { type ReactNode } from 'react'
import { ChevronLeft, ChevronRight, Inbox, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { Paginated } from '@/lib/types'

export function PaginationBar<T>({
  meta,
  onPage,
}: {
  meta: Paginated<T>['meta']
  onPage: (page: number) => void
}) {
  if (!meta || meta.total === 0) return null
  return (
    <div className="flex items-center justify-between border-t border-border px-2 py-3">
      <p className="text-sm text-muted-foreground">
        Showing <span className="font-medium text-foreground">{meta.from ?? 0}</span>–
        <span className="font-medium text-foreground">{meta.to ?? 0}</span> of{' '}
        <span className="font-medium text-foreground">{meta.total}</span>
      </p>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={meta.current_page <= 1}
          onClick={() => onPage(meta.current_page - 1)}
        >
          <ChevronLeft className="h-4 w-4" />
          Previous
        </Button>
        <span className="px-2 text-sm text-muted-foreground">
          Page {meta.current_page} of {meta.last_page}
        </span>
        <Button
          variant="outline"
          size="sm"
          disabled={meta.current_page >= meta.last_page}
          onClick={() => onPage(meta.current_page + 1)}
        >
          Next
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}

export function EmptyState({
  title,
  description,
  action,
  icon: Icon = Inbox,
}: {
  title: string
  description?: string
  action?: ReactNode
  /** Optional lucide icon component. Defaults to Inbox. */
  icon?: React.ElementType
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
      <Icon className="h-10 w-10 text-muted-foreground/40" />
      <div>
        <p className="font-medium">{title}</p>
        {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
      </div>
      {action}
    </div>
  )
}

export function ErrorState({
  message,
  onRetry,
}: {
  message?: string
  onRetry?: () => void
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
      <AlertCircle className="h-10 w-10 text-destructive/60" />
      <div>
        <p className="font-medium">Something went wrong</p>
        <p className="mt-1 text-sm text-muted-foreground">
          {message ?? 'Failed to load data. Please try again.'}
        </p>
      </div>
      {onRetry && (
        <Button variant="outline" size="sm" onClick={onRetry}>
          Retry
        </Button>
      )}
    </div>
  )
}
